import { default as React, useEffect, useState } from 'react';
import {
  DevHubAutocomplete,
  DevHubAutocompleteChangeReason,
  DevHubBox,
  DevHubButton,
  DevHubDialog,
  DevHubDialogActions,
  DevHubDialogContent,
  DevHubDialogTitle,
  DevHubDynamicIcon,
  DevHubGrid,
  DevHubIconButton,
  DevHubPaper,
  DevHubTextField,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { gql, useLazyQuery } from '@apollo/client';
import { debounce } from 'debounce';
import { InformationClassificationPolicy } from '../insights/types/PolicyTypes';

const InformationClassificationPolicyQuery = gql`
  query GetInformationClassificationPolicy {
    resources {
      informationClassificationPolicies {
        name
        description
      }
    }
  }
`;

interface Props {
  groupEnabled?: boolean;
  className?: any;
  name?: string;
  helperText?: string;
  size?: 'medium' | 'small';
  setSelectedParty: React.Dispatch<
    React.SetStateAction<InformationClassificationPolicy>
  >;
  selectedParty?: InformationClassificationPolicy;
  required?: boolean;
  label?: string;
}

/**
 * Search for a party or group bases on prop isUser
 * @param props Props of the component
 * @param props.groupEnabled Set to true to allow searching of groups and enabled the toggle
 * @param props.setSelectedParty Setter hook for value selected in form
 * @param props.name name value passed to the component
 */
export function ClassificationSelect({
  className,
  name = '',
  size = 'medium',
  setSelectedParty,
  selectedParty = {} as InformationClassificationPolicy,
  required = true,
  label,
}: Props) {
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const [policies, setPolicies] = useState<InformationClassificationPolicy[]>(
    [],
  );

  const [getPolicies, { loading: policiesLoading, error: policiesError }] =
    useLazyQuery(InformationClassificationPolicyQuery, {
      errorPolicy: 'all',
    });

  const handleSearch = debounce(() => {
    getPolicies({})
      .then(userResults => {
        setPolicies(
          userResults?.data?.resources?.informationClassificationPolicies,
        );
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Error fetching policies', error);
      });
  }, 500);

  useEffect(() => {
    handleSearch();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedParty?.name) {
      setSelectedParty({ name: 'Confidential Information', description: '' });
    }
  }, [selectedParty, setSelectedParty]);

  // Event Handler to set selected party from a drop down
  const onSelect = (
    _: Object,
    value: InformationClassificationPolicy | null,
    reason: DevHubAutocompleteChangeReason,
  ) => {
    if (reason === 'clear' && value) {
      setSelectedParty(value);
    }
    if (!value) {
      return;
    }
    setSelectedParty(value);
  };

  return (
    <DevHubGrid container alignItems="center">
      <DevHubGrid item xs={10}>
        {policiesError ? (
          <>
            <DevHubTextField
              error
              label="Error"
              className={className}
              defaultValue="No policies Available"
              variant="outlined"
            />
          </>
        ) : (
          <DevHubAutocomplete
            className={className}
            loading={policiesLoading}
            loadingText="Loading.."
            options={policies}
            size={size}
            onChange={onSelect}
            defaultValue={
              selectedParty?.name
                ? selectedParty
                : { name: 'Confidential Information', description: '' }
            }
            getOptionLabel={option => option?.name}
            renderInput={params => (
              <DevHubTextField
                {...params}
                label={label ?? 'Select an information classification policy'}
                InputLabelProps={
                  required ? { required: true } : { required: false }
                }
                margin="normal"
                name={name}
                variant="outlined"
                required
                InputProps={params.InputProps}
              />
            )}
          />
        )}
      </DevHubGrid>
      <DevHubGrid item xs={2}>
        <DevHubBox ml={1}>
          <DevHubIconButton color="default" onClick={handleClickOpen}>
            <DevHubDynamicIcon iconName="Help" fontSize="medium" />
          </DevHubIconButton>
        </DevHubBox>
        <DevHubDialog maxWidth="md" open={open} onClose={handleClose}>
          <DevHubDialogTitle id="alert-dialog-title">
            What information classification policy do I need?
          </DevHubDialogTitle>
          <DevHubDialogContent>
            {policies?.map(policy => (
              <DevHubGrid container>
                <DevHubGrid item xs>
                  {policy.name}
                </DevHubGrid>
                <DevHubGrid item xs>
                  {policy.description}
                </DevHubGrid>
              </DevHubGrid>
            ))}
          </DevHubDialogContent>

          <DevHubPaper>
            <DevHubTypography>
              Please check{' '}
              <a
                target="_blank"
                href="https://comcast.zavanta.com/website/document/0a2bbd36-0553-4dc8-98f9-f15059616d35/0c0f2d76-e7c1-4086-be49-a45c7e4f7d38/a8376318-ebd6-421f-be63-acf8c88376a1?v=21726"
              >
                here
              </a>{' '}
              for more information on classification policies
            </DevHubTypography>
          </DevHubPaper>
          <DevHubDialogActions>
            <DevHubButton
              onClick={handleClose}
              color="primary"
              variant="contained"
            >
              close
            </DevHubButton>
          </DevHubDialogActions>
        </DevHubDialog>
      </DevHubGrid>
    </DevHubGrid>
  );
}
